import { IStairCalculationResult, IStairConfig } from '@stair-craft/common/util';

import {
    EColorRef,
    EPrimitiveKind,
    ETextAlign,
    ETextBaseline,
    FONT_MONO_FAMILY,
    IFont,
    IPaneRect,
    IPlanPrimitive,
    IPoint,
    IPolyPrimitive,
    IProjectionOutput,
    ITextPrimitive,
    emptyProjection,
} from './plan-primitive';
import { ITextMeasurer } from './text-measurer';

/**
 * The tallest a label stands across its line, pixels: the larger of the screen's 8 px font and
 * the DXF's 2.5 mm text (9.45 px on paper), so the labels keep apart in both.
 */
const LABEL_HEIGHT: number = 10;
/** Air between two labels, pixels (about 1 mm on paper). */
const LABEL_AIR: number = 4;
/** The closest two labels of one column may stand, centre to centre, pixels. */
const LABEL_PITCH: number = LABEL_HEIGHT + LABEL_AIR;
/** Gap between a label and the axis it annotates, pixels. */
const LABEL_GAP: number = 4;
/** The run a leader takes across, from a run label moved off its tread to the tread's row, pixels. */
const LEADER_RUN: number = 12;
/** A label this close to its row needs no leader, pixels. */
const LEADER_MIN_SHIFT: number = 0.5;
const PAD_RIGHT: number = 14;
const PAD_TOP: number = 20;
const PAD_BOTTOM: number = 14;
const MIN_INNER: number = 40;

const DIM_FONT_SMALL: IFont = { weight: 400, size: 8, family: FONT_MONO_FAMILY };

// Проекторы объявлены свойствами-функциями, а не методами: их разбирают
// деструктуризацией (`const { wx, wy } = projector`), и отвязанный от объекта
// метод потерял бы `this`.
interface IProjector {
    readonly wx: (x: number) => number;
    readonly wy: (y: number) => number;
}

function line(from: IPoint, to: IPoint, stroke: EColorRef, width: number): IPolyPrimitive {
    return { kind: EPrimitiveKind.Poly, points: [from, to], closed: false, stroke, width };
}

function filledTriangle(a: IPoint, b: IPoint, c: IPoint, fill: EColorRef): IPolyPrimitive {
    return { kind: EPrimitiveKind.Poly, points: [a, b, c], closed: true, fill };
}

function text(at: IPoint, value: string, align: ETextAlign, color: EColorRef, font: IFont, rotation?: number): ITextPrimitive {
    return { kind: EPrimitiveKind.Text, text: value, baseline: ETextBaseline.Middle, at, align, color, font, rotation };
}

/**
 * X (run) coordinate of step i's right edge. For L/U the cascade shifts to flight 2's
 * own X system after flight 1. Extracted so `projectStepsSchema` stays under its limits.
 */
function stepRightX(i: number, isLU: boolean, f1: number, b: number, sw: number): number {
    if (!isLU || f1 === 0) {
        return (i + 1) * b;
    }
    if (i < f1) {
        return (i + 1) * b;
    }
    return sw + (i - f1) * b;
}

/** X (run) coordinate of step i's left edge (sawtooth cascade). Extracted from `projectStepsSchema`. */
function stepLeftX(i: number, isLU: boolean, f1: number, b: number, sw: number): number {
    if (i === 0) {
        return 0;
    }
    if (isLU && i === f1) {
        return sw - b;
    }
    return stepRightX(i, isLU, f1, b, sw) - b;
}

/** What the step labels need from the schema's frame. */
interface ILabelCtx {
    stepCount: number;
    h: number;
    b: number;
    treadT: number;
    scale: number;
    originX: number;
    originY: number;
    isLU: boolean;
    f1: number;
    wx: (x: number) => number;
    wy: (y: number) => number;
    stepXCoord: (i: number) => number;
    /** How far the second row of rise labels stands above the first, pixels. */
    rowInset: number;
    /** Whether the run labels are spread apart from their rows and joined to them by leaders. */
    fanned: boolean;
}

/** Every `stride`-th step carries a label, and the last one always does. */
function labelled(i: number, stride: number, stepCount: number): boolean {
    return (i + 1) % stride === 0 || i === stepCount - 1;
}

/** Step `i` within its own flight: an L/U flight 2 counts from its first step and writes one row higher. */
function flightOf(i: number, ctx: ILabelCtx): { index: number; count: number; secondRow: boolean } {
    const split: boolean = ctx.isLU && ctx.f1 > 0;
    if (!split) {
        return { index: i, count: ctx.stepCount, secondRow: false };
    }
    return i >= ctx.f1
        ? { index: i - ctx.f1, count: ctx.stepCount - ctx.f1, secondRow: true }
        : { index: i, count: ctx.f1, secondRow: false };
}

/** Rows of rise labels over the top axis: two for an L/U stair, whose flight 2 restarts the run axis. */
function labelRows(isLU: boolean): number {
    return isLU ? 2 : 1;
}

/**
 * The rise labels over the top axis. Where the steps stand closer than a label is tall, only
 * every n-th step of a flight is labelled (its last one always), so the labels never run into
 * each other.
 */
function pushRiseLabels(primitives: IPlanPrimitive[], ctx: ILabelCtx): void {
    const stride: number = Math.max(1, Math.ceil(LABEL_PITCH / Math.max(1e-6, ctx.b * ctx.scale)));
    for (let i: number = 0; i < ctx.stepCount; i++) {
        const flight: { index: number; count: number; secondRow: boolean } = flightOf(i, ctx);
        if (!labelled(flight.index, stride, flight.count)) {
            continue;
        }
        const rowY: number = ctx.originY - LABEL_GAP - (flight.secondRow ? ctx.rowInset : 0);
        const value: string = String(Math.round((i + 1) * ctx.h));
        primitives.push(text([ctx.wx(ctx.stepXCoord(i)), rowY], value, ETextAlign.Left, EColorRef.Ink3, DIM_FONT_SMALL, -Math.PI / 2));
    }
}

/**
 * Where labels of one column stand when their rows are closer than a label is tall: each at
 * least a pitch below the one before, then the whole run moved up by half of what the last one
 * was pushed down, so the column stays centred on its rows; never above `top`.
 */
function spreadLabels(rows: readonly number[], top: number): number[] {
    const spread: number[] = [];
    rows.forEach((row: number, index: number): void => {
        spread.push(index === 0 ? row : Math.max(row, spread[index - 1] + LABEL_PITCH));
    });
    const last: number = rows.length - 1;
    const lift: number = last < 0 ? 0 : Math.min((spread[last] - rows[last]) / 2, Math.max(0, spread[0] - top));
    return spread.map((at: number): number => at - lift);
}

/**
 * The run labels left of the rise axis, one column. Where the treads stand closer than a label is
 * tall, the labels spread apart along the column and a leader joins each to its tread's row, so
 * every number is read against its own step; only where even the spread column would run far
 * past the schema is every n-th one left out, the last always kept.
 */
function pushRunLabels(primitives: IPlanPrimitive[], ctx: ILabelCtx): void {
    const height: number = ctx.stepCount * ctx.h * ctx.scale + 2 * LABEL_PITCH;
    const stride: number = ctx.fanned ? Math.max(1, Math.ceil((ctx.stepCount * LABEL_PITCH) / height)) : 1;
    const shown: number[] = Array.from({ length: ctx.stepCount }, (_unused: unknown, i: number): number => i).filter((i: number): boolean =>
        labelled(i, stride, ctx.stepCount)
    );
    const rows: number[] = shown.map((i: number): number => ctx.wy((i + 1) * ctx.h - ctx.treadT));
    const places: number[] = ctx.fanned ? spreadLabels(rows, ctx.originY + LABEL_HEIGHT / 2) : rows;
    const labelX: number = ctx.originX - LABEL_GAP - (ctx.fanned ? LEADER_RUN : 0);
    shown.forEach((i: number, index: number): void => {
        primitives.push(
            text([labelX, places[index]], String(Math.round(ctx.stepXCoord(i))), ETextAlign.Right, EColorRef.Ink3, DIM_FONT_SMALL)
        );
        if (Math.abs(places[index] - rows[index]) > LEADER_MIN_SHIFT) {
            primitives.push(line([labelX + LABEL_GAP / 2, places[index]], [ctx.originX, rows[index]], EColorRef.Ink3, 0.5));
        }
    });
}

/** The widest of the labels, pixels. */
function widestLabel(values: readonly string[], measurer: ITextMeasurer): number {
    return Math.max(0, ...values.map((value: string): number => measurer.measure(value, DIM_FONT_SMALL)));
}

/** The room the run label column takes left of the rise axis, pixels: with the leaders' run when they are spread. */
function runLabelRoom(fanned: boolean, labelWidth: number): number {
    return LABEL_GAP + labelWidth + (fanned ? LEADER_RUN : 0) + LABEL_GAP;
}

/** What the schema's frame is fitted to: the labels it carries and the extent it draws. */
interface ISchemaContent {
    rises: string[];
    runs: string[];
    /** Rows of rise labels: two for an L/U stair. */
    rows: number;
    /** One riser, millimetres: how far apart the run labels stand. */
    rise: number;
    /** The longest run and the full rise, millimetres. */
    run: number;
    height: number;
}

/** The schema's fit: its scale, the axis corner and how its labels are set out. */
interface ISchemaFrame {
    scale: number;
    originX: number;
    originY: number;
    rowInset: number;
    fanned: boolean;
}

/**
 * The frame of the schema, sized for its labels as measured: the rows of rise labels above the
 * top axis, each clear of the longest label of the row below (an L/U stair's flight 2 restarts
 * the run axis, so its rise labels take a second row), and the run labels left of the rise axis
 * in one column, spread with leaders when the treads stand closer than a label is tall. Null
 * when the pane is too small to draw in.
 */
function schemaFrame(pane: IPaneRect, content: ISchemaContent, measurer: ITextMeasurer): ISchemaFrame | null {
    const riseWidth: number = widestLabel(content.rises, measurer);
    const runWidth: number = widestLabel(content.runs, measurer);
    const originY: number = PAD_TOP + LABEL_GAP + content.rows * riseWidth + (content.rows - 1) * LABEL_AIR;
    const innerH: number = pane.h - PAD_BOTTOM - originY;
    const fitFor: (fanned: boolean) => number = (fanned: boolean): number =>
        Math.min((pane.w - PAD_RIGHT - runLabelRoom(fanned, runWidth)) / content.run, innerH / content.height);
    const fanned: boolean = content.rise * fitFor(false) < LABEL_PITCH;
    const originX: number = runLabelRoom(fanned, runWidth);
    if (pane.w - PAD_RIGHT - originX < MIN_INNER || innerH < MIN_INNER) {
        return null;
    }
    return { originX, originY, fanned, scale: fitFor(fanned), rowInset: riseWidth + LABEL_AIR };
}

/**
 * Steps schema (схема ступеней) — the cascade: every tread as a sawtooth rectangle
 * plotted against a top (run) and left (rise) axis, each annotated with its
 * cumulative X (run) and Y (rise) dimension arrows. For L/U the cascade shifts to
 * flight 2's own X system after flight 1. Pure projection in pane-local pixels.
 * 1:1 port of the former PlanViewComponent#drawStepsSchema.
 */
export function projectStepsSchema(
    result: IStairCalculationResult,
    config: IStairConfig,
    pane: IPaneRect,
    measurer: ITextMeasurer
): IProjectionOutput {
    const stepCount: number = result.stepCount;
    const h: number = result.riserHeight;
    const b: number = result.treadDepth;
    const totalRise: number = result.room.floorHeight;
    const treadT: number = config.treadThickness;
    const sw: number = config.stairWidth;

    if (stepCount <= 0) {
        return emptyProjection();
    }

    const isLU: boolean = config.type === 'l-shape' || config.type === 'u-shape';
    const f1: number = result.flight1Count ?? 0;

    // X coordinate per step — for L/U the cascade shifts to flight 2's own X system after flight 1.
    const stepXCoord: (i: number) => number = (i: number): number => stepRightX(i, isLU, f1, b, sw);

    let maxXCoord: number = 0;
    for (let i: number = 0; i < stepCount; i++) {
        maxXCoord = Math.max(maxXCoord, stepXCoord(i));
    }
    if (maxXCoord <= 0) {
        return emptyProjection();
    }

    const steps: number[] = Array.from({ length: stepCount }, (_unused: unknown, i: number): number => i);
    const frame: ISchemaFrame | null = schemaFrame(
        pane,
        {
            rises: steps.map((i: number): string => String(Math.round((i + 1) * h))),
            runs: steps.map((i: number): string => String(Math.round(stepXCoord(i)))),
            rows: labelRows(isLU),
            rise: h,
            run: maxXCoord,
            height: totalRise,
        },
        measurer
    );
    if (!frame) {
        return emptyProjection();
    }
    const { scale, originX, originY, rowInset, fanned }: ISchemaFrame = frame;
    const projector: IProjector = {
        wx: (x: number): number => originX + x * scale,
        wy: (y: number): number => originY + y * scale,
    };
    const { wx, wy }: IProjector = projector;

    const primitives: IPlanPrimitive[] = [];

    // Top axis + left axis.
    primitives.push(line([originX, originY], [wx(maxXCoord) + 6, originY], EColorRef.Ink3, 0.8));
    primitives.push(line([originX, originY], [originX, wy(totalRise) + 6], EColorRef.Ink3, 0.8));

    // Sawtooth tread rectangles.
    for (let i: number = 0; i < stepCount; i++) {
        const xRight: number = stepXCoord(i);
        const xLeft: number = stepLeftX(i, isLU, f1, b, sw);
        const xL: number = wx(Math.max(0, xLeft));
        const xR: number = wx(xRight);
        const yT: number = wy((i + 1) * h - treadT);
        const yB: number = wy((i + 1) * h);
        primitives.push({
            kind: EPrimitiveKind.Poly,
            points: [
                [xL, yT],
                [xR, yT],
                [xR, yB],
                [xL, yB],
            ],
            closed: true,
            fill: EColorRef.Step,
            stroke: EColorRef.StepStroke,
            width: 1,
        });
    }

    // Per-step dimension arrows (X = cumulative run, Y = cumulative rise).
    for (let i: number = 0; i < stepCount; i++) {
        const xCoord: number = stepXCoord(i);
        const xEnd: number = wx(xCoord);
        const yTreadTop: number = wy((i + 1) * h - treadT);

        // Y-arrow (vertical, from top axis down to the tread top).
        primitives.push(line([xEnd, originY], [xEnd, yTreadTop], EColorRef.Ink3, 0.5));
        primitives.push(line([xEnd - 2, originY], [xEnd + 2, originY], EColorRef.Ink3, 0.5));
        primitives.push(filledTriangle([xEnd, yTreadTop], [xEnd - 2, yTreadTop - 4], [xEnd + 2, yTreadTop - 4], EColorRef.Ink3));

        // X-arrow (horizontal, from left axis to the step's X).
        primitives.push(line([originX, yTreadTop], [xEnd, yTreadTop], EColorRef.Ink3, 0.5));
        primitives.push(line([originX, yTreadTop - 2], [originX, yTreadTop + 2], EColorRef.Ink3, 0.5));
        primitives.push(filledTriangle([xEnd, yTreadTop], [xEnd - 4, yTreadTop - 2], [xEnd - 4, yTreadTop + 2], EColorRef.Ink3));
    }

    const labelCtx: ILabelCtx = {
        stepCount,
        h,
        b,
        treadT,
        scale,
        originX,
        originY,
        isLU,
        f1,
        wx,
        wy,
        stepXCoord,
        rowInset,
        fanned,
    };
    pushRiseLabels(primitives, labelCtx);
    pushRunLabels(primitives, labelCtx);

    // Millimetre origin: the axis corner (run = 0, rise = 0); both axes grow down/right the screen.
    return { primitives, pxPerMm: scale, origin: { x: originX, y: originY } };
}
